// FlipYard eBay listing generator — FlipYard ONLY. No shared code with Aquagga.
// Roger's real listing format (from his "car part ebay descriptions" .docx
// templates): keyword-dense title + "ACT FAST FLASH SALES" body with fitment
// caveat, PART/Pulled From/VIN/CONDITION header, packing/shipping/returns blocks,
// and his sign-off.
//
// Built to win in BOTH search worlds at once (Roger 2026-08-15):
//   TODAY  = eBay Best Match is still keyword + fitment + listing quality. Two
//            listings with the same number don't both surface unless the number is
//            in the TITLE (not just the specifics), the side is right, and fitment
//            is filled. So the title carries the number WHOLE (never truncated) and
//            the side; every structured field gets filled.
//   SOON   = AI answer/shopping agents (AEO/GEO) read the structured attributes and
//            the plain-language facts, not the hype. So the specifics are complete
//            and the body answers the buyer's real questions (fits? side? tested?
//            OEM? what's included?).
//
// Pure function (type-only import) so the client listing builder can call it too. It must stay free
// of any node-bound module (oem-format.ts pulls node:fs, which crashes the client bundle), so the few
// helpers it needs are inlined here as pure client-safe versions.
import type { Vehicle } from "./ebay-search";

const normNum = (s: string) => (s ?? "").replace(/[^0-9a-z]/gi, "").toUpperCase();

// Sided parts (mirror of oem-format.SIDED): left and right are different parts.
const SIDED = /head ?la?mp|head ?light|tail ?la?mp|tail ?light|mirror|fog|fender|quarter panel|\bdoor\b|caliper|control arm|\baxle\b|window regulator|window motor|\bstrut\b|knuckle|\bseat\b(?! belt)/i;
function sideFromTitle(title: string): "left" | "right" | null {
  const t = (title ?? "").toLowerCase();
  const left = /\b(left|driver'?s?|lh|l\/h)\b/.test(t);
  const right = /\b(right|passenger'?s?|rh|r\/h)\b/.test(t);
  if (left === right) return null;
  return left ? "left" : "right";
}
// Compact OEM-shape gate (client-safe subset of oem-format.matchesFormat): keep real part numbers,
// drop chassis codes, body styles, year sequences, and model names typed into the number.
function looksLikeOemNumber(make: string | undefined, model: string | undefined, n: string): boolean {
  n = normNum(n);
  if (n.length < 6 || n.length > 15) return false;
  if (!/[0-9]/.test(n)) return false;
  if (/^(.)\1+$/.test(n)) return false;                       // all one char
  if (/^((19|20)\d\d)+$/.test(n)) return false;               // year sequences
  const mk = normNum(make ?? ""); if (mk.length >= 3 && n.includes(mk)) return false;
  const md = normNum(model ?? ""); if (md.length >= 4 && n.includes(md)) return false;
  if (/^(19|20)\d\d[A-Z]/.test(n)) return false;              // year-prefixed label
  return true;
}
// A-prefix / bare variants so a buyer matches whether or not they type the leading letter (Mercedes A-form).
function numberVariants(make: string | undefined, n: string): string[] {
  n = normNum(n);
  const bare = n.replace(/^[AB](?=\d{6,})/, "");
  const out = new Set<string>([bare]);
  if (bare !== n) out.add(n);
  else if (/MERCEDES|BENZ/i.test(make ?? "") && /^\d{6,}$/.test(bare)) out.add(`A${bare}`);
  return [...out];
}

export type ListingInput = {
  vehicle: Vehicle;
  partName: string;
  partNumber?: string;
  interchange?: string[];      // additional OEM #s off the part (alternate fitments)
  superseded?: string[];       // old numbers this part replaced — highest-value cross refs to a buyer with an old diagram
  side?: "left" | "right" | "front" | "rear" | null; // explicit override; otherwise read off the part name
  condition?: string;          // e.g. "Used - tested, works"
  pulledFrom?: string;         // donor vehicle / VIN
  vin?: string;                // the full 17-char VIN off the door jamb
  paintCode?: string;          // paint code / color off the door jamb sticker
  price?: number | null;       // suggested Buy It Now (the lowest live comp)
  fits?: { year?: number; make: string; model: string }[]; // every vehicle this part fits — our own compatibility chart
  features?: string;           // honest selling points from the seller's own inspection (capture flow)
  conditionClass?: string;     // eBay structured condition: "Used" | "For parts or not working"
  hypeTitle?: boolean;         // prepend the 🔥 to the title (OFF by default — title chars are for searched words/numbers)
  credit?: boolean;            // append the eBay-compliant "Listing created by FlipYard" credit (OFF by default)
};

// A part number goes into the TITLE whole or not at all — a number cut in half by the
// 80-char cap matches nothing (or the wrong part). So we pack the title from richest to
// leanest and return the first arrangement that fits, and every arrangement keeps the
// number intact. Only the OPTIONAL words (qualifier, side, the word "OEM", extra part
// words) get dropped to make room; the year/make/model and the number never get cut.
function fitTitle(ymm: string, qualifier: string, part: string, side: string, number: string, max = 80): string {
  const firstWord = part.split(/\s+/)[0] || part;
  const join = (arr: string[]) => arr.filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
  const candidates = [
    [ymm, qualifier, part, side, "OEM", number],
    [ymm, qualifier, part, side, number],   // drop the literal word "OEM" (it's in the specifics)
    [ymm, part, side, "OEM", number],        // drop the qualifier
    [ymm, part, side, number],
    [ymm, part, "OEM", number],              // drop the side word (still in Placement specific)
    [ymm, part, number],
    [ymm, firstWord, number],                // lean the part down to its head noun
    [ymm, number],                           // last resort: the car + the number
  ];
  for (const c of candidates) { const s = join(c); if (s.length <= max) return s; }
  return join([ymm, number]).slice(0, max);  // ymm + number alone is ~40 chars, so this never cuts a number in practice
}

export type CompatRow = { year?: number; make: string; model: string; trim?: string; engine?: string };

export function generateListing(i: ListingInput): { title: string; description: string; itemSpecifics: Record<string, string>; compatibility: CompatRow[] } {
  const { year, make, model, fuelType, engine, trim, driveType, bodyClass, transmission } = i.vehicle;
  const ymm = [year, make, model].filter(Boolean).join(" ");
  // The side is added to the title as its own word, so strip any side word already inside the part
  // name (else "Left Headlight" + side "Left" prints "Headlight Left ... Left"). Keep the head noun.
  const partClean = i.partName.replace(/\b(left|right|driver'?s?|passenger'?s?|lh|rh|l\/h|r\/h|front|rear)\b/gi, " ").replace(/\s+/g, " ").trim() || i.partName;
  const partTitleCase = partClean.replace(/\b\w/g, (c) => c.toUpperCase());

  // SIDE — left/right is a different part and a top return + search factor. Take an explicit
  // override, else read it off the part name, but only claim a side for parts that actually have one.
  const isSided = SIDED.test(i.partName);
  const side = i.side ?? (isSided ? sideFromTitle(i.partName) : null);
  const sideWordLR = side === "left" ? "Left" : side === "right" ? "Right" : side === "front" ? "Front" : side === "rear" ? "Rear" : "";
  const sidePlacement = side === "left" ? "Left (Driver Side)" : side === "right" ? "Right (Passenger Side)" : sideWordLR;

  // Clean the numbers the SAME way we clean OEM numbers — a chassis code (L405), a body style
  // ("2door"), or a fragment must never reach the title or the Interchange field (they'd surface
  // the listing for the wrong part = returns + eBay keyword-spam penalty).
  const primary = i.partNumber ? normNum(i.partNumber) : "";
  // Stricter than the identity gate: for the discoverability fields a 6-char floor kills the body
  // styles ("2DOOR"), chassis codes (L405, X152), and fragments that matchesFormat's 5-char floor lets
  // through. Better to drop a borderline number than surface the listing for the wrong part.
  const cleanExtra = (arr?: string[]) =>
    (arr ?? []).map((n) => normNum(n)).filter((n) => n && n.length >= 6 && n !== primary && looksLikeOemNumber(make, model ? String(model) : undefined, n) && !/^\d?(DOOR|DR|WD|CYL|SPD|IN|MM|CM)$/.test(n));
  const interchangeClean = [...new Set(cleanExtra(i.interchange))];
  const supersededClean = [...new Set(cleanExtra(i.superseded))];
  // Widen keyword reach: include the letter-prefix / VAG-base variants of every clean number so a
  // buyer surfaces us whether or not they type the leading letter (same law as the Mercedes A-form).
  const forms = (n: string) => numberVariants(make, n);
  const interchangeField = [...new Set(interchangeClean.flatMap(forms))].slice(0, 20);
  const supersededField = [...new Set(supersededClean.flatMap(forms))].slice(0, 20);

  // A short disambiguating qualifier only when it earns its place (variant traps + AWD/fuel).
  const variantTag = (i.partName.match(/\b(AFS|HID|LED|Xenon|Halogen)\b/i) || [])[0];
  const qualRaw = (variantTag || (trim && trim.length <= 10 ? trim : "") || (engine && engine.length <= 8 ? engine : "") || "").trim();
  // Don't repeat a word the part name already carries (part "Headlight AFS" + qualifier "AFS").
  const qualifier = qualRaw && new RegExp(`\\b${qualRaw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i").test(partClean) ? "" : qualRaw;

  const titleCore = fitTitle(ymm, qualifier, partTitleCase, sideWordLR, primary, 80);
  const title = i.hypeTitle && ("🔥 " + titleCore).length <= 80 ? "🔥 " + titleCore : titleCore;

  // Everything the VIN told us — printed in the body so a buyer (or an agent) can match their car.
  const carFacts: string[] = [];
  if (trim) carFacts.push(`Trim: ${trim}`);
  if (engine) carFacts.push(`Engine: ${i.vehicle.turbo ? `${engine} turbo` : engine}`);
  if (fuelType) carFacts.push(`Fuel: ${fuelType}`);
  if (driveType) carFacts.push(`Drive: ${driveType}`);
  if (transmission) carFacts.push(`Transmission: ${transmission}`);
  if (bodyClass) carFacts.push(`Body: ${bodyClass}`);
  if (i.paintCode) carFacts.push(`Paint Code / Color: ${i.paintCode}`);

  const allNumbers = [primary, ...interchangeClean, ...supersededClean].filter(Boolean);
  const numbersLine = allNumbers.length ? `OEM / Interchange #s: ${allNumbers.slice(0, 20).join(", ")}` : "";
  const sideLine = sidePlacement ? `Side / Placement: ${sidePlacement}` : "";
  const fitmentLine = "Use the eBay compatibility chart to confirm fitment for your vehicle.";

  // Our OWN compatibility chart, built from the part's fitment data — the donor car plus every other
  // vehicle the OEM number fits. This is the table eBay leaves blank on used parts; we fill it.
  const fitsSeen = new Set<string>();
  const fitsList = [ymm, ...(i.fits ?? []).map((f) => [f.year, f.make, f.model].filter(Boolean).join(" "))]
    .map((s) => s.trim()).filter((s) => { const k = s.toLowerCase(); if (!s || fitsSeen.has(k)) return false; fitsSeen.add(k); return true; });
  const fitsBlock = fitsList.length > 1
    ? "This part fits these vehicles:\n" + fitsList.map((s) => `• ${s}`).join("\n") + "\n"
    : "";

  const description =
`${i.hypeTitle === false ? "" : "🔥🔥🔥 ACT FAST FLASH SALES 🔥🔥🔥\n\nFlash Sales DO NOT happen on every item. When this one sells at this price, we move the price up to match market conditions. BUY IT NOW.\n\n"}PART: ${i.partName}${sideLine ? `\n${sideLine}` : ""}
Fits: ${ymm}${i.vin ? `\nPulled From VIN: ${i.vin}` : ""}
Pulled From: ${i.pulledFrom || ymm}${year ? `\nDonor Year: ${year}` : ""}
${numbersLine ? numbersLine + "\n" : ""}${i.features ? "√√√ FEATURES\n" + i.features + "\n\n" : ""}CONDITION: ${i.condition || "Used OEM, in good working condition"}
${carFacts.length ? "\n√√√ THIS CAR (from the VIN)\n" + carFacts.join("\n") + "\n" : ""}
√√√ FITMENT
${fitsBlock}${fitmentLine}
The OEM number is the sure match — read it off your old part and compare. ${supersededClean.length ? `This part also replaces the older number(s): ${supersededClean.slice(0, 6).join(", ")}. ` : ""}Some electronic parts may need to be coded/programmed to your vehicle.

√√√ PACKING & SHIPPING
FAST shipping (USPS / FedEx). Carefully packed to arrive safe.

√√√ RETURNS
30-day money-back. If it doesn't fix your problem, return it — and tell us it tested good on your car (that helps us both).

√√√ LIFE IS SHORT, HAVE FUN AND STOP WASTING TIME √√√
BUY IT NOW AND THANK YOU FOR YOUR PURCHASE √√√`;

  // eBay "item specifics" — the structured fields buyers filter on and agents read. Pre-filled from
  // the VIN + the part so the seller just confirms them.
  const itemSpecifics: Record<string, string> = {};
  if (make) itemSpecifics["Brand"] = make;
  // Discrete Make/Model/Year — Cassini serves Motors listings into year/make/model filtered
  // search off these structured fields, so fill them, not just the "Fits" text line.
  if (make) itemSpecifics["Make"] = make;
  if (model) itemSpecifics["Model"] = String(model);
  if (year) itemSpecifics["Year"] = String(year);
  if (primary) itemSpecifics["Manufacturer Part Number"] = primary;
  if (interchangeField.length) itemSpecifics["Interchange Part Number"] = interchangeField.join(", ");
  if (supersededField.length) itemSpecifics["Superseded Part Number"] = supersededField.join(", ");
  itemSpecifics["OEM/Aftermarket"] = "Original Equipment Manufacturer (OEM)";
  if (sidePlacement) itemSpecifics["Placement on Vehicle"] = sidePlacement;
  if (ymm) itemSpecifics["Fits"] = ymm;
  if (fitsList.length > 1) itemSpecifics["Compatible Vehicles"] = fitsList.slice(0, 40).join(" | ");
  if (i.vin) itemSpecifics["Donor VIN"] = i.vin;
  if (trim) itemSpecifics["Trim"] = trim;
  if (engine) itemSpecifics["Engine"] = i.vehicle.turbo ? `${engine} turbo` : engine;
  if (fuelType) itemSpecifics["Fuel Type"] = fuelType;
  if (i.paintCode) itemSpecifics["Paint Code / Color"] = i.paintCode;
  itemSpecifics["Condition"] = i.conditionClass || i.condition || "Used";

  // Structured compatibility — THE Motors ranking lever (a part with no fitment is invisible in
  // year/make/model filtered search). The donor carries trim + engine; the fitment chart rows carry
  // year/make/model. eBay allows up to 1,000 combos, so cap there. This is what a poster pushes into
  // eBay's Parts Compatibility, separate from the "Compatible Vehicles" text line above.
  const compatSeen = new Set<string>();
  const compatibility: CompatRow[] = [];
  const pushCompat = (r: CompatRow) => {
    const k = `${r.year ?? ""}|${r.make.toLowerCase()}|${r.model.toLowerCase()}`;
    if (!r.make || !r.model || compatSeen.has(k)) return;
    compatSeen.add(k); compatibility.push(r);
  };
  if (make && model) pushCompat({ year: year ? Number(year) : undefined, make, model: String(model), trim: trim || undefined, engine: engine || undefined });
  for (const f of i.fits ?? []) if (f.make && f.model) pushCompat({ year: f.year, make: f.make, model: f.model });

  // eBay-compliant third-party credit: <=10 words, no URL, no contact, no promo — just identifies the
  // service (Links policy third-party-credit carve-out). OFF unless the caller opts in.
  const creditLine = i.credit ? "\n\nListing created by FlipYard" : "";
  return { title, description: description + creditLine, itemSpecifics, compatibility: compatibility.slice(0, 1000) };
}
